import { computed, type ComputedRef, reactive } from 'vue';
import { defineStore } from 'pinia';
import { DataState, makeDataState, type StateHolder, withStateLoading } from '@/util/dataState';
import { type ApiUserObject, getCurrentUser } from '@/api/me';
import { planAct } from '@/domain/plan/planAct';
import { taggedLogger } from '@/util/taggedLogger';

const logger = taggedLogger('user-store');

export type CurrentUserState = {
    user: ApiUserObject | null;
} & StateHolder;

export enum HipRoleNames {
    Admin = 'admin',
    OrgAdmin = 'org_admin',
    Surgeon = 'surgeon',
    SurgeonAssist = 'surgeon_assist',
    Sales = 'sales',
    SurgicalSupportEngineer = 'surgical_support_engineer',
}

const APPROVE_PLAN_ROLES = [HipRoleNames.Admin, HipRoleNames.OrgAdmin, HipRoleNames.Surgeon];
const PROPOSE_PLAN_ROLES = [HipRoleNames.Sales, HipRoleNames.SurgeonAssist];
const ORDER_SURGICAL_GUIDE_ROLES = [HipRoleNames.Admin, HipRoleNames.Surgeon];
const EDIT_CR_NUMBER_ROLES = [HipRoleNames.Sales, HipRoleNames.Admin];

/**
 * What the caller's role name alone decides.
 *
 * The plan act is one of them: a user holds one role, and the API refuses anything the role does
 * not reach, so the client names the act and the server remains the thing that enforces it.
 *
 * A sales rep may also order a surgical guide for a plan of a kind they prepared or already
 * ordered, which is a fact about the plan rather than the caller, so that arm stays where the
 * plan is known.
 */
function roleCapabilities(userRoles: ComputedRef<HipRoleNames[]>) {
    const holdsAny = (wanted: HipRoleNames[]) =>
        computed(() => wanted.some((role) => userRoles.value.includes(role)));

    const canApprovePlan = holdsAny(APPROVE_PLAN_ROLES);
    const canProposePlan = holdsAny(PROPOSE_PLAN_ROLES);

    return {
        canEditCrNumber: holdsAny(EDIT_CR_NUMBER_ROLES),
        mayOrderSurgicalGuide: holdsAny(ORDER_SURGICAL_GUIDE_ROLES),
        canApprovePlan,
        canProposePlan,
        planAct: computed(() => planAct(canApprovePlan.value, canProposePlan.value)),
    };
}

const makeInitialState = (): CurrentUserState => ({
    user: null,
    ...makeDataState(),
});

export const useUserStore = defineStore('user-store', () => {
    const state = reactive<CurrentUserState>(makeInitialState());

    const userRoles = computed<HipRoleNames[]>(() => state.user?.roles.map((r) => r.name) ?? []);

    const role = computed<HipRoleNames | null>(() => {
        if (userRoles.value.length === 0) {
            return null;
        }
        return userRoles.value[0];
    });

    const isAdmin = computed(() => userRoles.value.includes(HipRoleNames.Admin));
    const isOrgAdmin = computed(() => userRoles.value.includes(HipRoleNames.OrgAdmin));
    const isSurgeon = computed(() => userRoles.value.includes(HipRoleNames.Surgeon));
    const isSurgeonAssist = computed(() => userRoles.value.includes(HipRoleNames.SurgeonAssist));
    const isSales = computed(() => userRoles.value.includes(HipRoleNames.Sales));
    const isSurgicalSupportEngineer = computed(() =>
        userRoles.value.includes(HipRoleNames.SurgicalSupportEngineer),
    );

    const capabilities = roleCapabilities(userRoles);

    const isLoaded = computed(() => state.state !== DataState.Loading);

    const userId = computed(() => {
        if (state.user !== null) {
            return state.user.id.toString();
        }
        return '';
    });

    // Expose user as a getter for consumers that read store.user directly
    const user = computed({
        get: () => state.user,
        set: (val) => {
            state.user = val;
        },
    });

    async function load(): Promise<void> {
        await withStateLoading(state, async () => {
            state.user = await getCurrentUser();
        });
    }

    function reset(): void {
        logger.debug('Resetting current user information...');
        Object.assign(state, makeInitialState());
    }

    return {
        user,
        userRoles,
        role,
        isAdmin,
        isOrgAdmin,
        isSurgeon,
        isSurgeonAssist,
        isSales,
        isSurgicalSupportEngineer,
        ...capabilities,
        isLoaded,
        userId,
        load,
        reset,
    };
});
